export type UserRole = 'admin' | 'sani' | 'operator' | 'check_in' | 'camp_manager';

export const ROLE_LABELS: Record<UserRole, string> = {
    admin: 'Admin',
    sani: 'Sani',
    operator: 'Operator',
    check_in: 'Check-In',
    camp_manager: 'Camp Manager',
};

export const ALL_ROLES: UserRole[] = ['admin', 'sani', 'operator', 'check_in', 'camp_manager'];

export type NavItemDef = {
    href: string;
    label: string;
    key: string;
};

export const NAV_ITEMS: NavItemDef[] = [
    {href: '/registrations', label: 'Registrations', key: 'registrations'},
    {href: '/check-in', label: 'Check-In', key: 'check-in'},
    {href: '/staff', label: 'Staff', key: 'staff'},
    {href: '/newsletter', label: 'Newsletter', key: 'newsletter'},
    {href: '/documents', label: 'Documents', key: 'documents'},
    {href: '/statistics', label: 'Statistics', key: 'statistics'},
    {href: '/archive', label: 'Archive', key: 'archive'},
];

export const USERS_NAV_ITEM: NavItemDef = {
    href: '/users',
    label: 'Users',
    key: 'users',
};

export const SETTINGS_NAV_ITEM: NavItemDef = {
    href: '/settings',
    label: 'Settings',
    key: 'settings',
};

const NAV_KEYS_BY_ROLE: Record<UserRole, string[]> = {
    admin: ['registrations', 'check-in', 'staff', 'newsletter', 'documents', 'statistics', 'archive'],
    sani: ['registrations', 'statistics', 'archive'],
    operator: ['registrations', 'check-in', 'staff', 'newsletter', 'documents', 'statistics', 'archive'],
    check_in: ['check-in', 'documents', 'statistics', 'archive'],
    camp_manager: ['documents', 'statistics', 'archive'],
};

export function canManageSiteSettings(role: UserRole): boolean {
    return role === 'admin' || role === 'operator' || role === 'camp_manager';
}

export function canAccessDocuments(role: UserRole): boolean {
    return (
        role === 'admin'
        || role === 'operator'
        || role === 'check_in'
        || role === 'camp_manager'
    );
}

export const HOME_PATH_BY_ROLE: Record<UserRole, string> = {
    admin: '/registrations',
    sani: '/registrations',
    operator: '/registrations',
    check_in: '/check-in',
    camp_manager: '/statistics',
};

export function normalizeRole(role: string | undefined | null): UserRole {
    if (
        role === 'admin'
        || role === 'sani'
        || role === 'operator'
        || role === 'check_in'
        || role === 'camp_manager'
    ) {
        return role;
    }
    return 'operator';
}

export function getNavItemsForRole(role: UserRole): NavItemDef[] {
    const allowed = new Set(NAV_KEYS_BY_ROLE[role]);
    return NAV_ITEMS.filter((item) => allowed.has(item.key));
}

export function canAccessPath(role: UserRole, path: string): boolean {
    const allowedPaths = getNavItemsForRole(role).map((item) => item.href);
    if (path === '/' || path === '/registrations') {
        return allowedPaths.includes('/registrations');
    }
    if (path === '/passkey') {
        return true;
    }
    if (path === '/users') {
        return canManageUsers(role);
    }
    if (path === '/settings') {
        return canManageSiteSettings(role);
    }
    if (path === '/documents') {
        return canAccessDocuments(role);
    }
    return allowedPaths.some(
        (allowed) => path === allowed || path.startsWith(`${allowed}/`),
    );
}

export function canManageUsers(role: UserRole): boolean {
    return role === 'admin' || role === 'operator';
}

export function isAdminRole(role: UserRole): boolean {
    return role === 'admin';
}

export function getAssignableRoles(actorRole: UserRole): UserRole[] {
    if (actorRole === 'admin') {
        return ALL_ROLES;
    }
    if (actorRole === 'operator') {
        return ALL_ROLES.filter((role) => role !== 'admin');
    }
    return [];
}

export function canModifyUser(
    actorRole: UserRole,
    targetRole: UserRole,
    actorUsername: string,
    targetUsername: string,
): boolean {
    if (actorUsername === targetUsername) {
        return false;
    }
    if (isAdminRole(actorRole)) {
        return true;
    }
    if (canManageUsers(actorRole)) {
        return !isAdminRole(targetRole);
    }
    return false;
}

export function canManageNewsletter(role: UserRole): boolean {
    return role === 'admin' || role === 'operator';
}

export function canWriteRegistrations(role: UserRole): boolean {
    return role === 'admin' || role === 'operator';
}

export function canMoveToArchive(role: UserRole): boolean {
    return role === 'admin';
}

export function canTogglePaid(role: UserRole): boolean {
    return role === 'admin' || role === 'operator';
}

export function canManageStaff(role: UserRole): boolean {
    return role === 'admin' || role === 'operator';
}

export function canUseCheckIn(role: UserRole): boolean {
    return role === 'admin' || role === 'operator' || role === 'check_in';
}

export function canAccessNavKey(role: UserRole, navKey: string): boolean {
    return NAV_KEYS_BY_ROLE[role].includes(navKey);
}

export type RoleAccessRow = {
    label: string;
    hasAccess: (role: UserRole) => boolean;
};

export const ROLE_ACCESS_ROWS: RoleAccessRow[] = [
    {
        label: 'Registrations (view)',
        hasAccess: (role) => canAccessNavKey(role, 'registrations'),
    },
    {
        label: 'Registrations (edit)',
        hasAccess: canWriteRegistrations,
    },
    {
        label: 'Toggle paid status',
        hasAccess: canTogglePaid,
    },
    {
        label: 'Check-In',
        hasAccess: canUseCheckIn,
    },
    {
        label: 'Staff',
        hasAccess: canManageStaff,
    },
    {
        label: 'Newsletter',
        hasAccess: canManageNewsletter,
    },
    {
        label: 'Statistics',
        hasAccess: (role) => canAccessNavKey(role, 'statistics'),
    },
    {
        label: 'Archive (view)',
        hasAccess: (role) => canAccessNavKey(role, 'archive'),
    },
    {
        label: 'Move to archive',
        hasAccess: canMoveToArchive,
    },
    {
        label: 'Users',
        hasAccess: canManageUsers,
    },
    {
        label: 'Documents',
        hasAccess: canAccessDocuments,
    },
    {
        label: 'Settings',
        hasAccess: canManageSiteSettings,
    },
];
